import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ManagerColor,
  AdminUser,
  CreateUserInput,
  InvitationResult,
  ListResponse,
  Role,
} from "@salesboard/contracts";
import { useT } from "@/i18n";
import { api, toQueryString } from "@/lib/api";
import { reportMutationError, reportMutationSuccess } from "@/lib/mutations";

const KEY = "users";

export function useAdminUsers(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.get<ListResponse<AdminUser>>(`/users${toQueryString(params)}`),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api.post<{ user: AdminUser; invitation?: InvitationResult }>("/users", input),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(
        result.invitation?.emailSent
          ? t("toast.invitationSentTo", result.user.email)
          : t("toast.created", result.user.name),
      );
    },
    onError: (error) => reportMutationError(error),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, send }: { id: string; send: boolean }) =>
      api.post<InvitationResult>(`/users/${id}/invite`, { send }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(result.emailSent ? t("toast.invitationEmailSent") : t("toast.invitationLinkReady"));
    },
    onError: (error) => reportMutationError(error),
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/users/${id}/revoke-invitation`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(t("toast.invitationRevoked"));
    },
    onError: (error) => reportMutationError(error),
  });
}

export function useChangeUserRole() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, role, version }: { id: string; role: Role; version: number }) =>
      api.patch<AdminUser>(`/users/${id}/role`, { role, version }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(t("toast.roleChanged"));
    },
    onError: (error) => reportMutationError(error),
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      api.post<AdminUser>(`/users/${id}/set-active`, { active, version }),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(user.active ? t("toast.userReactivated") : t("toast.userDeactivated"));
    },
    onError: (error) => reportMutationError(error),
  });
}

/**
 * Assign or clear a manager's board colour.
 *
 * Invalidates the opportunity queries as well as the user list, because the colour is
 * painted on every card: leaving the board alone would show the old tint until something
 * else happened to refetch it.
 */
export function useSetUserColor() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; color: ManagerColor | null; version: number }) =>
      api.patch<AdminUser>(`/users/${id}/color`, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["opportunities"] }),
        queryClient.invalidateQueries({ queryKey: ["owners"] }),
      ]);
      reportMutationSuccess(t("toast.colourSaved"));
    },
    onError: (error: unknown) => reportMutationError(error),
  });
}
